import React, { useState, useEffect } from 'react';
import './../../../Styles/Cart/AddAddress.css';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axiosUserInstance from '../../../axiosUserInstance';
import CloseIcon from '../../../assets/svg/closeIcon';
import Loader from '../../../Components/Loader/Loader';

function AddNewAddress({ handleClose, getAddress, editAddress, existingAddresses }) {
    const [deliveryName, setDeliveryName] = useState('');
    const [streetName, setStreetName] = useState('');
    const [areaCityState, setAreaCityState] = useState('');
    const [state, setState] = useState('');
    const [zipcode, setZipcode] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (editAddress && Object.keys(editAddress).length > 0) {
            setDeliveryName(editAddress.Delivery_name || '');
            setStreetName(editAddress.Address_line_1 || '');
            setAreaCityState(editAddress.City || '');
            setState(editAddress.State || '');
            setZipcode(editAddress.ZIP || '');
        }
    }, [editAddress]);

    const getCoordinatesFromAddress = async (address) => {
        const apiKey = process.env.REACT_APP_GOOGLE_MAP_KEY;
        const encodedAddress = encodeURIComponent(address);
        const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${apiKey}`;

        try {
            const response = await fetch(url);
            const data = await response.json();
            if (data.status === 'OK' && data.results.length > 0) {
                const { lat, lng } = data.results[0].geometry.location;
                return { latitude: lat, longitude: lng };
            } else {
                console.error('Geocoding failed:', data.status);
                return null;
            }
        } catch (err) {
            console.error('Geocoding error:', err);
            return null;
        }
    };


    const validateInputs = () => {
        const newErrors = {};

        const nameRegex = /^[A-Za-z\s']{3,50}$/;
        const streetRegex = /^[A-Za-z0-9\s,.\-/#']{3,100}$/;
        const cityStateRegex = /^[A-Za-z\s,]{2,50}$/;
        const stateRegex = /^[A-Za-z\s()]{2,30}$/;
        const zipRegex = /^[A-Za-z0-9\s-]{4,10}$/;
        if (!deliveryName.trim()) {
            newErrors.deliveryName = 'Delivery name is required.';
        } else if (!nameRegex.test(deliveryName)) {
            newErrors.deliveryName = 'Use 3-50 letters only (e.g., Sarah O\'Connor).';
        }
    
        if (!streetName.trim()) {
            newErrors.streetName = 'Street name is required.';
        } else if (!streetRegex.test(streetName)) {
            newErrors.streetName = 'Use 3-100 letters, numbers, and special characters (,.-/\'#) (e.g., 45B Baker St. or Apt #5).';
        }
    
        if (!areaCityState.trim()) {
            newErrors.areaCityState = 'Area/City/State is required.';
        } else if (!cityStateRegex.test(areaCityState)) {
            newErrors.areaCityState = 'Use 2-50 letters only (e.g., Los Angeles).';
        }
    
        if (!state.trim()) {
            newErrors.state = 'State is required.';
        } else if (!stateRegex.test(state)) {
            newErrors.state = 'Use 2-30 letters only (e.g., Texas).';
        }
    
        if (!zipcode.trim()) {
            newErrors.zipcode = 'ZIP code is required.';
        } else if (!zipRegex.test(zipcode)) {
            newErrors.zipcode = 'Invalid ZIP (4-10 characters, e.g., 12345 or SW1A 1AA).';
        }
    
        setErrors(newErrors);
        return Object.keys(newErrors).length > 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (loading) return;
        const isValid = validateInputs();

        if (isValid) return;

        const fullAddress = `${streetName}, ${areaCityState}, ${state}, ${zipcode}`;
        const coords = await getCoordinatesFromAddress(fullAddress);

        if (!coords) {
            toast.error('Unable to get location. Please check address.');
            return;
        }

        const addressData = {
            Delivery_name: deliveryName,
            Address_line_1: streetName,
            Address_line_2: '',
            City: areaCityState,
            State: state,
            ZIP: zipcode,
            Latitude: coords.latitude,
            Longitude: coords.longitude,
        };

        const isDuplicate = existingAddresses.some((addr) => {
            return (
                addr.Delivery_name?.trim().toLowerCase() === deliveryName.trim().toLowerCase() &&
            addr.Address_line_1?.trim().toLowerCase() === streetName.trim().toLowerCase() &&
            addr.City?.trim().toLowerCase() === areaCityState.trim().toLowerCase() &&
            addr.State?.trim().toLowerCase() === state.trim().toLowerCase() &&
            addr.ZIP?.trim().toLowerCase() === zipcode.trim().toLowerCase() &&
            (!editAddress || addr.ID !== editAddress.ID)
            );
        });

        if (isDuplicate) {
            toast.error('This address already exists.');
            return;
        }

        try {
            setLoading(true);
            if (editAddress && editAddress.ID) {
                await axiosUserInstance.put(`address/account/${editAddress.ID}`, addressData);
            } else {
                await axiosUserInstance.post('address/account/account_id', addressData);
            }
            handleClose();
            await getAddress();
        } catch (error) {
            console.error('Error updating/adding address:', error);
            toast.error('Failed to save address. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            {loading && (
                <div>
                    <Loader/>
                </div>
            )}
            <div className='title-cross-svg-container'>
                <h1 className='add-new-address-title'>
                    {editAddress ? t('cart.edit-address') : t('cart.add-new-address-title')}
                </h1>
                <CloseIcon onClick={handleClose} className='cross-icon-location' />
            </div>

            <form onSubmit={handleSubmit}>
                <div className='delivery-name-container'>
                    <label className='name-label'>{t('cart.delivery-name-title')}</label>
                    <input
                        className='add-address-input'
                        value={deliveryName}
                        onChange={(e) => setDeliveryName(e.target.value)}
                    />
                    {errors.deliveryName && (
                        <p className='error-message'>{errors.deliveryName}</p>
                    )}
                </div>

                <div className='delivery-name-container'>
                    <label className='name-label'>{t('cart.street-title')}</label>
                    <input
                        className='add-address-input'
                        value={streetName}
                        onChange={(e) => setStreetName(e.target.value)}
                    />
                    {errors.streetName && (
                        <p className='error-message'>{errors.streetName}</p>
                    )}
                </div>

                <div className='delivery-name-container'>
                    <label className='name-label'>
                        {t('cart.area-and-city-and-state')}
                    </label>
                    <input
                        className='add-address-input'
                        value={areaCityState}
                        onChange={(e) => setAreaCityState(e.target.value)}
                    />
                    {errors.areaCityState && (
                        <p className='error-message'>{errors.areaCityState}</p>
                    )}
                </div>

                <div className='delivery-name-container'>
                    <label className='name-label'>State</label>
                    <input
                        className='add-address-input'
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                    />
                    {errors.state && <p className='error-message'>{errors.state}</p>}
                </div>

                <div className='delivery-name-container'>
                    <label className='name-label'>{t('cart.zip-code-title')}</label>
                    <input
                        className='add-address-input'
                        value={zipcode}
                        onChange={(e) => setZipcode(e.target.value)}
                    />
                    {errors.zipcode && <p className='error-message'>{errors.zipcode}</p>}
                </div>

                <button
                    type='submit'
                    className='add-address-btn'
                    disabled={loading}
                >
                    {editAddress ? t('cart.update-address-title') : t('cart.add-address-title')}
                </button>
            </form>
        </div>
    );
}

export default AddNewAddress;
